/**
 * Spaces API 클라이언트
 */
import apiClient from './client';

export interface SpaceMember {
  userId: string;
  email: string;
  name: string;
  role: 'owner' | 'editor' | 'viewer';
  joinedAt: string;
}

export interface SpaceFile {
  id: string;
  knowledgeBaseId: string;
  name: string;
  type: string;
  size: number;
  status: 'pending' | 'processing' | 'indexed' | 'error';
  s3Key: string;
  chunkCount?: number;
  createdAt: string;
  updatedAt: string;
  spaceId?: string;
  kbId?: string;
}

export interface Space {
  spaceId: string;
  spaceName: string;
  description: string;
  userId: string;
  userName: string;
  workflowId: string;
  members: SpaceMember[];
  fileCount: number;
  lastActivityAt: string;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

export interface SpaceSession {
  sessionId: string;
  chatId: string;        // '1' (세션 첫 번째 행)
  spaceId: string;
  senderEmail: string;
  senderName: string;
  userContent?: string;  // 첫 질문 (히스토리 타이틀용)
  title?: string;
  startDateTime: string;
  status: 'pending' | 'complete';
}

export interface SpaceMessage {
  sessionId: string;
  chatId: string;
  spaceId: string;
  senderEmail: string;
  senderName: string;
  userContent: string;
  assistantContent?: string;
  startDateTime: string;
  endDateTime?: string;
  inputToken?: number;
  outputToken?: number;
  totalToken?: number;
  resultTime?: string;
  status: 'pending' | 'complete';
  sources?: Array<{ documentName: string; score: number; excerpt?: string; url?: string }>;
  traceContent?: Array<Record<string, unknown>>;
  title?: string;
  ttl?: number;
}

const spacesApi = {
  /** 내 스페이스 목록 */
  async list(): Promise<Space[]> {
    const res = await apiClient.get<{ success: boolean; data: Space[] }>('/api/spaces');
    return res.data.data;
  },

  /** 스페이스 생성 */
  async create(data: {
    spaceName: string; description?: string;
    workflowId: string; inviteEmails?: string[];
  }): Promise<Space> {
    const res = await apiClient.post<{ success: boolean; data: Space }>('/api/spaces', data);
    return res.data.data;
  },

  /** 스페이스 상세 */
  async get(id: string): Promise<Space> {
    const res = await apiClient.get<{ success: boolean; data: Space }>(`/api/spaces/${id}`);
    return res.data.data;
  },

  /** 스페이스 수정 */
  async update(id: string, data: Partial<Pick<Space, 'spaceName' | 'description' | 'status'>>): Promise<Space> {
    const res = await apiClient.put<{ success: boolean; data: Space }>(`/api/spaces/${id}`, data);
    return res.data.data;
  },

  /** 스페이스 삭제 */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/spaces/${id}`);
  },

  // ── 세션 / 메시지 ────────────────────────────────────────────────────

  /** 스페이스 세션 목록 */
  async getSessions(spaceId: string): Promise<SpaceSession[]> {
    const res = await apiClient.get<{ success: boolean; data: SpaceSession[] }>(
      `/api/spaces/${spaceId}/sessions`
    );
    return res.data.data;
  },

  /** 세션 메시지 조회 */
  async getMessages(spaceId: string, sessionId: string): Promise<SpaceMessage[]> {
    const res = await apiClient.get<{ success: boolean; data: SpaceMessage[] }>(
      `/api/spaces/${spaceId}/sessions/${sessionId}`
    );
    return res.data.data;
  },

  // ── 멤버 ────────────────────────────────────────────────────────────────

  /** 멤버 추가 */
  async addMember(id: string, email: string, role: 'editor' | 'viewer' = 'editor'): Promise<SpaceMember> {
    const res = await apiClient.post<{ success: boolean; data: SpaceMember }>(
      `/api/spaces/${id}/members`, { email, role }
    );
    return res.data.data;
  },

  /** 멤버 제거 */
  async removeMember(id: string, email: string): Promise<void> {
    await apiClient.delete(`/api/spaces/${id}/members/${encodeURIComponent(email)}`);
  },

  /** 세션 제목 변경 */
  async renameSession(spaceId: string, sessionId: string, title: string): Promise<void> {
    await apiClient.put(`/api/spaces/${spaceId}/sessions/${sessionId}/rename`, { title });
  },

  /** 세션 삭제 */
  async deleteSession(spaceId: string, sessionId: string): Promise<void> {
    await apiClient.delete(`/api/spaces/${spaceId}/sessions/${sessionId}`);
  },

  // ── 파일 ────────────────────────────────────────────────────────────────

  /** 파일 업로드 */
  async uploadFile(spaceId: string, file: File): Promise<SpaceFile> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post<{ success: boolean; data: SpaceFile }>(
      `/api/spaces/${spaceId}/files`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return res.data.data;
  },

  /** 파일 목록 */
  async getFiles(spaceId: string): Promise<SpaceFile[]> {
    const res = await apiClient.get<{ success: boolean; data: SpaceFile[] }>(
      `/api/spaces/${spaceId}/files`
    );
    return res.data.data;
  },

  /** 파일 삭제 */
  async deleteFile(spaceId: string, fileId: string): Promise<void> {
    await apiClient.delete(`/api/spaces/${spaceId}/files/${fileId}`);
  },
};

export default spacesApi;
